import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { CountBar } from "./components/CountBar";
import { OrderItem } from "./components/OrderItem";
import BottomNav from "@/components/feature/BottomNav";
import { getOrderStats, getOrderList } from "@/api/staff";
import type { OrderStats, StaffOrderItem } from "@/api/staff";
import { useLoadMore } from "@/hooks/useLoadMore";

const BRAND = "#FF6B35";

// Tab 配置
const TABS = [
  { title: "全部", value: "" },
  { title: "待服务", value: 20 },
  { title: "进行中", value: 30 },
  { title: "待评价", value: 40 },
  { title: "已完成", value: 50 },
];

export default function WorkspaceOrderPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TABS[0].value);
  const [countData, setCountData] = useState<OrderStats | null>(null);

  // 使用公共 hook 处理分页滚动加载
  const {
    list,
    loading,
    hasMore,
    containerRef,
    reload,
  } = useLoadMore<StaffOrderItem>({
    fetcher: async (page) => {
      const res = await getOrderList({ status: String(activeTab), page, limit: 10 });
      return {
        list: res.list || [],
        total: res.count || 0,
        page: page,
        limit: 10,
      };
    },
    pageSize: 10,
    deps: [activeTab], // Tab 切换时重置列表
  });

  // 获取订单统计数据
  useEffect(() => {
    getOrderStats()
      .then((res) => setCountData(res))
      .catch(console.error);
  }, []);

  // 切换 Tab → reload
  const handleTabChange = (value: number) => {
    setActiveTab(value);
  };

  return (
    <div className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)] bg-gray-50">
      {/* 头部 */}
      <div className="relative pb-4 overflow-hidden"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(145deg, #1A1A2E 0%, #16213E 60%, #0F3460 100%)` }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4 px-5">
            <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 border border-white/20 cursor-pointer active:scale-95">
              <i className="ri-arrow-left-line text-white text-lg" />
            </button>
            <span className="text-base font-bold text-white">订单</span>
          </div>

          <div className="mt-5 px-3">
            {countData && <CountBar orderData={countData} />}
          </div>
        </div>
      </div>

      {/* Tab 栏 */}
      <div className="px-4 mt-6">
        <div className="bg-white rounded-full p-1 flex gap-1 shadow-sm">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-[0.99] ${
                activeTab === tab.value
                  ? "text-white shadow-sm"
                  : "text-[#464646] hover:bg-gray-50"
              }`}
              style={activeTab === tab.value ? { background: BRAND } : undefined}
            >
              {tab.title}
            </button>
          ))}
        </div>
      </div>

      {/* 订单列表 */}
      <div ref={containerRef} className="px-4 mt-4 min-h-[400px]" style={{ height: "calc(100vh - 280px)", overflowY: "auto" }}>
        {list.length > 0 ? (
          <div className="space-y-4">
            {list.map((item) => (
              <OrderItem key={item.id} item={item} />
            ))}
            {!hasMore && !loading && (
              <div className="py-4 text-center text-xs text-gray-300">— 已加载全部 —</div>
            )}
          </div>
        ) : null}

        {/* Loading 骨架屏 */}
        {loading && list.length === 0 && (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                <div className="h-4 w-48 rounded mb-3 bg-gray-100" />
                <div className="h-8 w-32 rounded bg-gray-100" />
              </div>
            ))}
          </div>
        )}

        {/* 加载更多 */}
        {loading && list.length > 0 && (
          <div className="py-4 text-center text-xs text-gray-400">
            <i className="ri-loader-4-line text-lg animate-spin block mx-auto mb-1" />
            加载更多...
          </div>
        )}

        {/* 空状态 */}
        {!loading && list.length === 0 && (
          <div className="flex flex-col items-center justify-center h-[300px] text-gray-400 text-sm">
            <i className="ri-file-list-3-line text-4xl mb-3" />
            暂无订单数据
          </div>
        )}
      </div>

      {/* 底部导航栏 */}
      <BottomNav />
    </div>
  );
}
